/*
 * lib/onboarding.js
 *
 * Eerste-keer-tutorial die de nieuwe gebruiker langs de belangrijkste
 * UI-elementen leidt: KPI-tegels, +-knop, tabs, account-dropdown, bell,
 * tandwiel. Bestaat los van panels.js — manipuleert direct de DOM:
 *
 *   - Eén full-screen backdrop met dimming
 *   - Een tooltip-card die geplaatst wordt naast het target
 *   - Highlight via een outline-ring op het target
 *
 * Stappen-lijst zit hieronder als constant. Een stap met target=null
 * toont 'm gecentreerd zonder highlight (intro + outro).
 *
 * Wordt aangeroepen vanuit main.js (na eerste render) wanneer
 * state.onboardingOpen true is. Cleanup op skip/finish + callback.
 */

const STEPS = [
  {
    title: '🌿 Welkom bij Florijn!',
    body: 'Een snelle rondleiding van ~1 minuut zodat je weet wat waar staat. Sla maar over als je liever zelf rondsnuffelt — je kan de tour later opnieuw starten via <strong>Instellingen → 📖 Handleiding → 🎓 Rondleiding opnieuw bekijken</strong>.',
    target: null,
    placement: 'center'
  },
  {
    title: 'Je kerncijfers',
    body: 'Bovenaan staan drie tegels: <strong>Vastlasten %</strong> (welk deel van je inkomen vast vastzit), <strong>Spaarquote</strong> (wat je deze maand overhoudt) en <strong>Runway</strong> (hoe lang je kan blijven leven van je noodfonds). Tik op het ⓘ-icoontje voor uitleg per tegel.',
    target: '.kpi-mini-row',
    placement: 'bottom'
  },
  {
    title: 'Een uitgave of inkomst toevoegen',
    body: 'De zwevende <strong>+ knop</strong> (rechtsonder, te verplaatsen naar links via Instellingen → User) opent op elk tabblad de toevoeg-modal. Op de tabs <em>Dromen</em>, <em>Inkomsten</em>, <em>Vaste</em> en <em>Variabele</em> vraagt hij eerst of het over een transactie of een nieuw item van dat type gaat.',
    target: '.fab',
    placement: 'top'
  },
  {
    title: 'Navigeer tussen tabs',
    body: 'Onderaan (of op desktop links als sidebar) switch je tussen <strong>Dromen</strong> (spaardoelen), <strong>Home</strong> (overzicht), <strong>Inkomsten</strong> en <strong>Uitgaven</strong>. Binnen Uitgaven zitten drie sub-tabs: <em>Extra</em>, <em>Vaste</em> en <em>Variabele</em>.',
    target: '.bottom-nav, #tabbar',
    placement: 'top'
  },
  {
    title: 'Wissel van account',
    body: 'Klik op je naam bovenaan om te switchen tussen je eigen Florijn en — indien aangezet — de <strong>Samen</strong>-weergave of je medebewoner. Hier vind je ook de uitloggen-knop.',
    target: '.psw-name',
    placement: 'bottom'
  },
  {
    title: 'Meldingen',
    body: 'De bel toont automatische reminders: gem-storting bevestigen, maandafsluit-reflectie, abonn-checks, en meer. Een rood randje = urgent.',
    target: 'button[title^="Meldingen"]',
    placement: 'bottom'
  },
  {
    title: 'Instellingen',
    body: 'Het tandwiel opent alle instellingen — account, huishouden, categorieën, thema, cashflow, belastingen. Onder <strong>📖 Handleiding</strong> vind je een volledige uitleg over Florijn én een knop om deze tour opnieuw te starten.',
    target: 'button[title="Instellingen"]',
    placement: 'bottom'
  },
  {
    title: '🎉 Klaar!',
    body: 'Je weet nu waar alles staat. Tijd om je eerste vaste lasten in te vullen — start in <strong>Inkomsten</strong> met je loon (anker-inkomst), daarna in <strong>Vast</strong> en <strong>Variabel</strong>. Veel succes!',
    target: null,
    placement: 'center'
  }
];

let currentIndex   = 0;
let backdropEl     = null;
let tooltipEl      = null;
let highlightedEl  = null;
let onCompleteFn   = null;

/** Start de tutorial. `onComplete` wordt geroepen op finish OF skip. */
export function startOnboarding(onComplete) {
  if (backdropEl) return;     // al actief — geen dubbele mount
  currentIndex = 0;
  onCompleteFn = onComplete || (() => {});
  mountChrome();
  showStep(0);
  window.addEventListener('resize', repositionCurrent);
  window.addEventListener('scroll', repositionCurrent, true);
}

function mountChrome() {
  backdropEl = document.createElement('div');
  backdropEl.className = 'onboarding-backdrop';
  document.body.appendChild(backdropEl);

  tooltipEl = document.createElement('div');
  tooltipEl.className = 'onboarding-tooltip';
  document.body.appendChild(tooltipEl);
}

function showStep(idx) {
  // Cleanup vorige highlight
  if (highlightedEl) {
    highlightedEl.classList.remove('onboarding-highlight');
    highlightedEl = null;
  }

  const step = STEPS[idx];
  if (!step) return finish();
  currentIndex = idx;

  // Vind target en zet highlight
  if (step.target) {
    highlightedEl = document.querySelector(step.target);
    if (highlightedEl) highlightedEl.classList.add('onboarding-highlight');
  }

  // Bouw tooltip
  const isFirst = idx === 0;
  const isLast  = idx === STEPS.length - 1;
  tooltipEl.innerHTML = `
    <div class="onboarding-progress">${idx + 1} / ${STEPS.length}</div>
    <h3 class="onboarding-title">${step.title}</h3>
    <div class="onboarding-body">${step.body}</div>
    <div class="onboarding-buttons">
      ${!isFirst ? '<button class="onboarding-btn-secondary" data-action="prev">Vorige</button>' : ''}
      ${!isLast  ? '<button class="onboarding-btn-skip" data-action="skip">Sla over</button>' : ''}
      <button class="onboarding-btn-primary" data-action="next">${isLast ? 'Klaar! 🌿' : 'Volgende'}</button>
    </div>
  `;

  positionTooltip(step, highlightedEl);

  // Bind buttons
  tooltipEl.querySelectorAll('button[data-action]').forEach(btn => {
    btn.addEventListener('click', () => {
      const a = btn.dataset.action;
      if (a === 'next')      isLast ? finish() : showStep(idx + 1);
      else if (a === 'prev') showStep(idx - 1);
      else if (a === 'skip') finish();
    });
  });
}

/** Positioneer de tooltip ten opzichte van het target. */
function positionTooltip(step, target) {
  tooltipEl.style.position = 'fixed';

  if (!target || step.placement === 'center') {
    tooltipEl.style.top       = '50%';
    tooltipEl.style.left      = '50%';
    tooltipEl.style.right     = 'auto';
    tooltipEl.style.bottom    = 'auto';
    tooltipEl.style.transform = 'translate(-50%, -50%)';
    return;
  }

  const rect = target.getBoundingClientRect();
  const tooltipRect = tooltipEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 12;

  tooltipEl.style.transform = 'none';

  // Vertikale placement: top of bottom
  if (step.placement === 'top') {
    const top = rect.top - tooltipRect.height - margin;
    tooltipEl.style.top    = (top < margin ? rect.bottom + margin : top) + 'px';
  } else {
    // default: bottom
    const top = rect.bottom + margin;
    const wouldOverflow = top + tooltipRect.height > vh - margin;
    tooltipEl.style.top    = (wouldOverflow ? Math.max(margin, rect.top - tooltipRect.height - margin) : top) + 'px';
  }

  // Horizontale placement: centreer rondom target, maar clamp binnen viewport
  let left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
  left = Math.max(margin, Math.min(left, vw - tooltipRect.width - margin));
  tooltipEl.style.left   = left + 'px';
  tooltipEl.style.right  = 'auto';
  tooltipEl.style.bottom = 'auto';
}

function repositionCurrent() {
  if (!tooltipEl) return;
  const step = STEPS[currentIndex];
  positionTooltip(step, highlightedEl);
}

function finish() {
  if (highlightedEl) highlightedEl.classList.remove('onboarding-highlight');
  if (backdropEl)    backdropEl.remove();
  if (tooltipEl)     tooltipEl.remove();
  window.removeEventListener('resize', repositionCurrent);
  window.removeEventListener('scroll', repositionCurrent, true);
  backdropEl = null;
  tooltipEl = null;
  highlightedEl = null;
  if (onCompleteFn) {
    const cb = onCompleteFn;
    onCompleteFn = null;
    cb();
  }
}
